import { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { SearchableCombobox, TSearchable } from '../base/combobox'
import { EventItemSelectorQuery, SelectedEventItemSelectorQuery } from './__generated__/selector_query'
import { Query, SelectedQuery } from './selector_query'
import { dedupe } from '@/helpers/dedupe'
import { useCombobox } from '@/helpers/useCombobox'

type AssetType = SelectedEventItemSelectorQuery['assetTypes']['nodes'][0]
type Package = SelectedEventItemSelectorQuery['packages']['nodes'][0]
type RowItem = SelectedEventItemSelectorQuery['rowItems']['nodes'][0]
type Vehicle = SelectedEventItemSelectorQuery['vehicles']['nodes'][0]

interface Props {
  value: string
  onChange: (value: TSearchable | null) => void
  invalid: boolean
}

export const Selector = ({ value, onChange, invalid }: Props) => {
  const { data: selectedData } = useQuery<SelectedEventItemSelectorQuery>(
    SelectedQuery,
    { variables: { id: value.split(':')[1] || 'blank' } }
  )
  const { data, loading, refetch, fetchMore } = useQuery<EventItemSelectorQuery>(Query, { variables: { first: 10 } })

  const buildSearchable = (item: AssetType | Package | RowItem | Vehicle) => ({
    id: `${item.__typename}:${item.id}`,
    name: item.name,
    image: item.images[0]?.url || data?.companies.nodes[0]?.placeholderLogo?.url
  })

  const { pageInfo: assetTypePageInfo, items: assetTypes } = useCombobox<AssetType>({
    pageInfo: data?.assetTypes.pageInfo,
    querySelected: selectedData?.assetTypes.nodes[0],
    queryCollection: data?.assetTypes.nodes,
    buildSearchable
  })

  const { pageInfo: packagePageInfo, items: packages } = useCombobox<Package>({
    pageInfo: data?.packages.pageInfo,
    querySelected: selectedData?.packages.nodes[0],
    queryCollection: data?.packages.nodes,
    buildSearchable
  })

  const { pageInfo: rowItemPageInfo, items: rowItems } = useCombobox<RowItem>({
    pageInfo: data?.rowItems.pageInfo,
    querySelected: selectedData?.rowItems.nodes[0],
    queryCollection: data?.rowItems.nodes,
    buildSearchable
  })

  const { pageInfo: vehiclePageInfo, items: vehicles } = useCombobox<Vehicle>({
    pageInfo: data?.vehicles.pageInfo,
    querySelected: selectedData?.vehicles.nodes[0],
    queryCollection: data?.vehicles.nodes,
    buildSearchable
  })

  const [items, setItems] = useState<TSearchable[]>([])

  useEffect(() => {
    if (!data) return

    setItems(
      dedupe([
        ...assetTypes,
        ...packages,
        ...rowItems,
        ...vehicles
      ]).sort((a, b) => ('' + a.name).localeCompare(b.name))
    )
  }, [data, assetTypes, packages, rowItems, vehicles])

  return (
    <SearchableCombobox
      data={items}
      filterFields={['assetTypeFilters', 'packageFilters', 'rowItemFilters', 'vehicleFilters']}
      includeBlank={false}
      invalid={invalid}
      loading={loading}
      placeholder="What's next on the list? Start typing to search"
      refetch={refetch}
      searchField="nameCont"
      value={items.find(i => i.id == value) || { id: '', name: '' }}
      fetchMore={() => {
        const allItems = {
          assetType: assetTypes.length,
          package: packages.length,
          rowItem: rowItems.length,
          vehicle: vehicles.length
        }
        const maxItems = Object.entries(allItems)
          .reduce((most, [key, count]) => count > allItems[most]
            ? key as keyof typeof allItems
            : most as keyof typeof allItems, 'assetType' as keyof typeof allItems)
        const pageInfos = {
          assetType: assetTypePageInfo,
          package: packagePageInfo,
          rowItem: rowItemPageInfo,
          vehicle: vehiclePageInfo,
        }

        return fetchMore({
          variables: { after: pageInfos[maxItems].current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.assetTypes.nodes = dedupe([
              ...previousData.assetTypes.nodes,
              ...fetchMoreResult.assetTypes.nodes
            ])
            fetchMoreResult.packages.nodes = dedupe([
              ...previousData.packages.nodes,
              ...fetchMoreResult.packages.nodes
            ])
            fetchMoreResult.rowItems.nodes = dedupe([
              ...previousData.rowItems.nodes,
              ...fetchMoreResult.rowItems.nodes
            ])
            fetchMoreResult.vehicles.nodes = dedupe([
              ...previousData.vehicles.nodes,
              ...fetchMoreResult.vehicles.nodes
            ])

            return { ...fetchMoreResult }
          }
        })
      }}
      moreToFetch={
        assetTypePageInfo.current?.hasNextPage
        || packagePageInfo.current?.hasNextPage
        || rowItemPageInfo.current?.hasNextPage
        || vehiclePageInfo.current?.hasNextPage
      }
      onChange={onChange}
    />
  )
}
